import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { appActions } from '../../../store';
import { useUrlSync } from '../../../hooks/useUrlSync';
import { useActiveProfile } from '../../../hooks/useActiveProfile';
import ProductList from '../../shared/ProductList/ProductList';
import CustomizeColors from '../CustomizeColors/CustomizeColors';
import styles from "./ProductMenu.module.css";
import { getThumbnailUrl } from '../../../Helpers/ThumbHelper';
import left from './assets/left.svg';

// Generate swatch URL using patternColors currentId values
const getSwatchUrl = (chosenPattern, patternColors) => {
    if (!chosenPattern || !patternColors || patternColors.length === 0) return null;

    const defaultRoomSceneSpecifier = chosenPattern.defaultRoomSceneSpecifier;
    if (!defaultRoomSceneSpecifier) return null;

    const cutLoopString = defaultRoomSceneSpecifier
        .split(',')
        .map(item => item.trim() === 'L' ? '@LOOP' : '@CUT')
        .join(',');

    const selString = patternColors
        .map(color => `!${color.currentId}`)
        .join(',');

    return `https://renderfarm2.chameleonpower.com/proxy/getImage.ashx?spec=${chosenPattern.specifier}(APPLY=(SCALE=1,COLORS=(${selString}),TEXTURES=(${cutLoopString})),FOCUS=(@SIZE[1]/(@PATRES[1]|@RES[1])))&size=1024&extra=(SIZE=(?,(@SIZE[1]*@PATRES[2]/@PATRES[1])))`;
};

export default function ProductMenu() {
    const [colorSearchTerm, setColorSearchTerm] = useState('');
    const dispatch = useDispatch();
    
    useUrlSync();
    useActiveProfile();
    
    const style = useSelector(state => state.app.style);
    const chosenPattern = useSelector(state => state.app.chosenPattern);
    const selectedColorIndex = useSelector(state => state.app.selectedColorIndex);
    const patternColors = useSelector(state => state.app.patternColors);
    const activeSection = useSelector(state => state.app.menuActiveSection);

    const handleSectionClick = (section) => {
        dispatch(appActions.setMenuActiveSection(section));
    };

    const handleBackToStartPage = () => {
        dispatch(appActions.clearChosenPattern());
    };

    const handlePatternSelect = (product) => {
        dispatch(appActions.setChosenPattern(product));
        dispatch(appActions.setStyle(product.name || product.color || 'Selected'));
        dispatch(appActions.setSize(product.descriptor || '18x18')); // Set size from pattern descriptor
        dispatch(appActions.setProductSelection([-1, -1]));
        dispatch(appActions.setCurrentView('swatch'));
        dispatch(appActions.setMenuActiveSection('customize')); // Go back to customize after selecting pattern
    };

    const handleColorProductSelect = (product) => {
        console.log('Color product selected:', product);
        
        if (selectedColorIndex === null || !patternColors[selectedColorIndex]) {
            console.log('No color selected in CustomizeColors');
            return;
        }

        const selectedColor = patternColors[selectedColorIndex];
        dispatch(appActions.updatePatternColor({
            originalId: selectedColor.originalId,
            newProduct: product
        }));
        
        console.log('Updated color', selectedColor.originalId, 'to', product.id);
    };

    return (
        <section className={styles.productMenu}>
            <div className={styles.backHeader}>
                        <button 
                            className={styles.backButton}
                            onClick={handleBackToStartPage}
                            title="Back to start"
                        >
                            <span className={styles.backArrow}>
                                <img src={left} width="auto" height="15" alt="arrow left"/>
                            </span>
                            <span className={styles.backText}>Custom Carpet</span>
                        </button>
                    </div>

                    <div className={styles.subheader}>
                        <h2>Explore standard and custom options</h2>
                    </div>

                    <div className={styles.selectionGrid}>
                        {/* Pattern Section */}
                        <div 
                            className={`${styles.selectionCard} ${activeSection === 'pattern' ? styles.active : ''}`}
                            onClick={() => handleSectionClick('pattern')}
                            title="Select a pattern"
                        >
                            <div className={styles.cardImage}>
                                {chosenPattern ? (
                                    <img 
                                        src={getSwatchUrl(chosenPattern, patternColors) || getThumbnailUrl(chosenPattern)} 
                                        alt={chosenPattern.name}
                                        className={styles.cardImageImg}
                                    />
                                ) : (
                                    <div className={styles.stylePlaceholder}></div>
                                )}
                            </div>
                            <div className={styles.cardLabel}>Pattern</div>
                            <div className={styles.cardValue}>{chosenPattern?.name || 'Select'}</div>
                        </div>

                        {/* Customize Section */}
                        <div 
                            className={`${styles.selectionCard} ${activeSection === 'customize' ? styles.active : ''}`}
                            onClick={() => handleSectionClick('customize')}
                            title="Customize your rug"
                        >
                            <div className={styles.cardImage}>
                                <div className={styles.customizePlaceholder}>
                                    <img 
                                        src="/img/brush.svg" 
                                        alt="Customize"
                                        className={styles.sizeImage}
                                    />
                                </div>
                            </div>
                            <div className={styles.cardLabel}>Customize</div>
                            <div className={styles.cardValue}>{style || 'Select colors'}</div>
                        </div>

                    </div>

                    {/* Content Area */}
                    <div className={styles.contentArea}>
                        {activeSection === 'pattern' && (
                            <div className={styles.patternContent}>
                                <h3 className={styles.patternTitle}>Pick a Pattern</h3>
                                <ProductList 
                                    seriesId="9756"
                                    thumbSize="256"
                                    titleAttribute="name"
                                    descriptionAttribute="descriptor"
                                    sortBy="name"
                                    sortOrder="asc"
                                    onProductSelect={handlePatternSelect}
                                    selectedProductId={chosenPattern?.id}
                                />
                            </div>
                        )}

                        {activeSection === 'customize' && (
                            <div className={styles.customizeContent}>
                                <div className={styles.colorwaySection}>
                                    <p className={styles.sectionDescription}>
                                        Select a colorway and choose a new color to recolor the pattern
                                    </p>
                                    <CustomizeColors />
                                </div>

                                <div className={styles.colorSearchSection}>
                                    <div className={styles.colorSearchWrapper}>
                                        <input
                                            type="text"
                                            placeholder="Search by color"
                                            value={colorSearchTerm}
                                            onChange={(e) => setColorSearchTerm(e.target.value)}
                                            className={styles.colorSearchInput}
                                        />
                                        <span className={styles.colorSearchIcon} aria-hidden="true">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                                <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                                                <line x1="16.65" y1="16.65" x2="21" y2="21" stroke="currentColor" strokeWidth="2" />
                                            </svg>
                                        </span>
                                    </div>
                                    <div className={styles.colorProductList}>
                                        <ProductList 
                                            seriesId="8623"
                                            thumbSize="80"
                                            titleAttribute="sku"
                                            descriptionAttribute=""
                                            searchValue={colorSearchTerm}
                                            searchColumns={['name', 'color', 'sku', 'description']}
                                            onProductSelect={handleColorProductSelect}
                                            gridClassName={styles.colorGrid}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
        </section>
    );
}
